import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import { STATIC_STRINGS } from "@asez/shared";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { CELL_TYPES, FILTER_KEYS } from "@asez/shared";
import { formatDateTime, formatPercentage, isEmpty, copyToClipboard } from "../../../../utils";
import { Tooltip } from "@mui/material";
import useMediaQueryScreen from "../../../../hooks/useMediaQueryScreen";
import { TableVirtuoso, TableVirtuosoProps } from "react-virtuoso";

const RenderTable = ({
  filters,
  gridData,
  searchTerm,
  selectedRows,
  updateSelectedRows,
  visibleColumns,
  header
}: any) => {
  const divRef = useRef<any>(null);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [width, setWidth] = useState<number>(0);
  const { screen, screenProps } = useMediaQueryScreen();
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);  

  const updateWidth = () => {
    if (divRef.current) {
      setWidth(divRef.current.offsetWidth);
    }
  };
  const convertFormDataToGridData = (formFields) => {
    let res = {};
    formFields.map((formField) => {
      if (
        formField.type === CELL_TYPES.file ||
        formField.type === CELL_TYPES.url
      ) {
        res[`${formField.id}`] = generateCellData(
          formField.value,
          formField.type
        );
      } else {
        res[`${formField.id}`] = formField.value;
      }
    });
    return res;
  };

  const filteredData = useMemo(() => {
    if (!gridData) return [];

    const searchQuery = searchTerm.toLowerCase();
    const progressStatusFilter = filters[FILTER_KEYS.PROGRESS_STATUS];
    const userStatusFilter = filters[FILTER_KEYS.USER_STATUS];
    const trustScoreFilter = filters[FILTER_KEYS.TRUST_SCORE];
    const marksFilter = filters[FILTER_KEYS.MARKS];
    const strongAreasFilter = filters[FILTER_KEYS.STRONG_AREAS];

    return gridData
      .filter((candidate) => {
        const {
          name,
          progress,
          status,
          trustScore,
          scorePercentage,
          strongAreas,
        } = candidate;

        const candidateName = name.toLowerCase();
        const matchSearch = candidateName.includes(searchQuery);

        const matchProgress =
          !progressStatusFilter || progress === progressStatusFilter;

        const candidateStatus = status?.toUpperCase();
        const matchUserStatus =
          !userStatusFilter || candidateStatus === userStatusFilter;

        const candidateTrustScore = Number(trustScore * 100);
        const matchTrustScore =
          !trustScoreFilter ||
          (candidateTrustScore >= trustScoreFilter[0] &&
            candidateTrustScore <= trustScoreFilter[1]);

        const matchMarks =
          !marksFilter ||
          (scorePercentage === null &&
            marksFilter[0] === 0 &&
            marksFilter[1] === 100) ||
          (parseInt(scorePercentage) >= marksFilter[0] &&
            parseInt(scorePercentage) <= marksFilter[1]);

        const matchStrongAreas =
          !strongAreasFilter ||
          strongAreasFilter.length === 0 ||
          (Array.isArray(strongAreas) &&
            strongAreas.length > 0 &&
            strongAreasFilter.some((area) => strongAreas.includes(area)));

        return (
          matchSearch &&
          matchProgress &&
          matchUserStatus &&
          matchTrustScore &&
          matchMarks &&
          matchStrongAreas
        );
      })
      .map((datum) => {
        return {
          id: datum.id,
          name: generateCellData(datum.name, CELL_TYPES.text),
          email: generateCellData(datum.email, CELL_TYPES.text),
          candidateStatus: datum.status,
          totalMarks: generateCellData(
            datum.scorePercentage,
            CELL_TYPES.percent,
            STATIC_STRINGS.NOT_STARTED
          ),
          trustScore: generateCellData(
            formatPercentage(Number(datum.trustScore), 0),
            CELL_TYPES.percent
          ),
          addedOn: generateCellData(datum.createdAt, CELL_TYPES.dateTime),
          attemptedOn: generateCellData(
            datum.assessmentStartedTime,
            CELL_TYPES.dateTime
          ),
          areasOfImprovement: generateCellData(
            datum.weakAreas,
            CELL_TYPES.chip
          ),
          strongAreas: generateCellData(datum.strongAreas, CELL_TYPES.chip),
          ...convertFormDataToGridData(datum.formFields),
          assessmentPdfLink: generateCellData(
            datum.assessmentPdfLink || "",
            CELL_TYPES.text
          ),
        };
      });
  }, [searchTerm, gridData, filters]);

  const handleMenuClick = (event: React.MouseEvent, id: string) => {
    const rect = (
      event.currentTarget as HTMLButtonElement
    ).getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom,
      left: rect.right,
    });
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedId(null);
  };

  function generateCellData(cellData, type, emptyMsg?) {
    if (isEmpty(cellData)) {
      return emptyMsg ? (
        <span className="text-danger text-base font-medium">{emptyMsg}</span>
      ) : (
        <span>---</span>
      );
    }
    switch (type) {
      case CELL_TYPES.text:
        return <span className="text-base font-medium">{cellData}</span>;
      case CELL_TYPES.percent:
        return <span className="text-base font-medium">{cellData}%</span>;
      case CELL_TYPES.dateTime:
        return (
          <span className="text-base font-medium">
            {formatDateTime(cellData)}
          </span>
        );
      case CELL_TYPES.file:
      case CELL_TYPES.url:
        return (
          <button
            className="btn-primary px-2 py-1 rounded-lg"
            onClick={() =>
              window.open(cellData, "_blank", "noopener,noreferrer")
            }
          >
            View
          </button>
        );
      case CELL_TYPES.chip:
        return (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs font-medium chip">{cellData[0]}</span>
            {cellData.length > 1 && (
              <Tooltip title={cellData.slice(1).join(", ")}>
                <span className="text-base font-medium text-neutral-dark ">
                  +{cellData.length - 1}{" "}
                </span>
              </Tooltip>
            )}
          </div>
        );
      default:
        return <>{cellData}</>;
    }
  }

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const TableComponents: TableVirtuosoProps<any, unknown>["components"] = {
    Scroller: forwardRef((props, ref) => (
      <TableContainer {...props} ref={ref} sx={{width:"100%"}} />
    )),
    Table: (props) => <Table {...props} stickyHeader sx={{width:"100%"}} />,
    TableHead: forwardRef((props, ref) => (
      <TableHead
        {...props}
        ref={ref}
        sx={{
          backgroundColor: "var(--color-table-header)",
          "& .MuiTableCell-head": {
            backgroundColor: "var(--color-table-header)",
          },
          position: "sticky"
        }}
      />
    )),
    TableRow: ({ ...props }) => (
      <TableRow
        {...props}
      />
    ),
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
  };

  return (
    <TableVirtuoso
      style={{
        maxHeight: "calc(100% - 68px)",
        maxWidth: "100%",
      }}
      data={filteredData}
      components={TableComponents}
      fixedHeaderContent={() => (
        <>
            <TableCell
              sx={{
                left: "0px",
                zIndex: 10,
                width: "450px",
                minWidth: "450px",
                whiteSpace: "nowrap",
                paddingLeft: !screenProps.is_largeplus ? 0 : undefined,
              }}
            >
              <Checkbox
                onChange={(e) =>
                  updateSelectedRows(
                    e.target.checked ? gridData.map((d) => d.id) : []
                  )
                }
                sx={{
                  color: "var(--color-neutral-dark)",
                  "&.Mui-checked": {
                    color: "var(--color-neutral-dark)",
                  },
                }}
                checked={selectedRows.length === gridData.length}
              />
              <span className="text-base font-semibold">
                {STATIC_STRINGS.TABLE_HEADERS.CANDIDATE}
              </span>
            </TableCell>

            {visibleColumns.map((col) => (
              <TableCell
                key={col.key}
                id={col.key}
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: "200px",
                  maxWidth: "200px",
                }}
              >
                <div className="flex justify-center w-full">
                  <span className="text-base font-semibold">{col.label}</span>
                </div>
              </TableCell>
            ))}

            <TableCell
              sx={{
                right: "0px",
                zIndex: 10,
                width: "220px",
                minWidth: "220px",
                whiteSpace: "nowrap",
                paddingRight: !screenProps.is_largeplus ? 0 : undefined,
              }}
            >
              <div className="flex justify-center w-full">
                <span className="text-base font-semibold">
                  {STATIC_STRINGS.TABLE_HEADERS.ACTIONS}
                </span>
              </div>
            </TableCell>
          </>
      )}
      itemContent={(idx , row) => (
        <>
            <TableCell
              sx={{
                position: "sticky",
                left: "0px",
                width: "450px",
                minWidth: "450px",
                maxWidth: "450px",
                whiteSpace: "nowrap",
                backgroundColor: "white",
                borderBottom: idx == filteredData.length - 1 ? "none" : "",
                paddingLeft: !screenProps.is_largeplus ? 0 : undefined,
              }}
            >
              <Checkbox
                checked={selectedRows.includes(row.id)}
                sx={{
                  color: "var(--color-neutral-dark)",
                  "&.Mui-checked": {
                    color: "var(--color-neutral-dark)",
                  },
                }}
                onChange={(e) => {
                  if (e?.target?.checked) {
                    updateSelectedRows([...selectedRows, row.id]);
                  } else {
                    updateSelectedRows(
                      selectedRows.filter((id) => id !== row.id)
                    );
                  }
                }}
              />
              {row.name}( {row.email}) 
              {row.candidateStatus && (<span className="mx-1 active">{row.candidateStatus}</span>)}
            </TableCell>
            {visibleColumns.map((col) => (
              <TableCell
                key={`${col.key}-${row.id}`}
                id={`${col.key}-${row.id}`}
                sx={{
                  borderBottom: idx == filteredData.length - 1 ? "none" : "",
                }}
              >
                <div className="flex justify-center w-full">
                  {row[col.key]}
                </div>
              </TableCell>
            ))}

            <TableCell
              sx={{
                position: "sticky",
                right: "0px",
                width: "220px",
                minWidth: "220px",
                whiteSpace: "nowrap",
                backgroundColor: "white",
                borderBottom: idx == filteredData.length - 1 ? "none" : "",
                paddingRight: !screenProps.is_largeplus ? 0 : undefined,
              }}
            >
              <div className="flex items-center justify-end w-full">
                <button
                  className={classNames(
                    "mr-2 btn btn-outline h-8 xl:h-10 flex items-center justify-center"
                  )}
                  onClick={() => {
                    navigate(`/candidate-report/${row.id}`)
                  }}
                >
                  <span className="text-xs xl:text-base">
                    {STATIC_STRINGS.VIEW_REPORT}
                  </span>
                </button>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(e, row.id);
                  }}
                >
                  <MoreVert />
                </IconButton>
                {selectedId === row.id && (
                  <Menu
                    open={!!menuPosition}
                    anchorReference="anchorPosition"
                    anchorPosition={menuPosition ?? { top: 0, left: 0 }}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    onClose={(e: Event) => {
                      e.stopPropagation();
                      handleMenuClose();
                    }}
                  >
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/edit/${selectedId}`);
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.EDIT}
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        const obj = gridData.find((grid) => grid.id===row.id)
                        copyToClipboard(obj.assessmentPdfLink);
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.COPY_REPORT_LINK}
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("button clicked");
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.SHORTLIST_CANDIDATE}
                    </MenuItem>
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("button clicked");
                      }}
                    >
                      {STATIC_STRINGS.MENU_ITEMS.REJECT_CANDIDATE}
                    </MenuItem>
                    <MenuItem
                    >
                      {STATIC_STRINGS.MENU_ITEMS.ALLOWED_ATTEMPTS}({header.allowedAttempts})
                    </MenuItem>
                  </Menu>
                )}
                </div>
              </TableCell>
        </>
      )}
    />
  )
};

export default RenderTable;
